import { useCallback, useEffect, useRef } from "react";
import { useFormikContext } from "formik";
import { useTheme } from "@mui/material/styles";
import emailMisspelled, { top100 } from "email-misspelled";

export const EMAIL_MISSPELLED_ERROR = "MISSPELLED";

const emailChecker = emailMisspelled({ domains: top100 });

interface UseEmailTypoWarningOptions {
  fieldName?: string;
}

export const useEmailTypoWarning = ({ fieldName = "email" }: UseEmailTypoWarningOptions = {}) => {
  const theme = useTheme();
  const { getFieldMeta, getFieldProps, setFieldError, setFieldTouched } = useFormikContext();
  const getFieldPropsRef = useRef(getFieldProps);
  useEffect(() => {
    getFieldPropsRef.current = getFieldProps;
  });
  const fieldError = getFieldMeta(fieldName).error;
  const isMisspelled = fieldError === EMAIL_MISSPELLED_ERROR;

  const onBlur = useCallback(
    (event?: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      // Mark field as touched since we're replacing Formik's default onBlur
      void setFieldTouched(fieldName, true, false);

      const value = event?.target?.value ?? (getFieldPropsRef.current(fieldName).value as string | undefined);
      if (value && emailChecker(value).length > 0) {
        setFieldError(fieldName, EMAIL_MISSPELLED_ERROR);
      } else if (fieldError === EMAIL_MISSPELLED_ERROR) {
        // Only clear if we previously set it — don't clobber real Yup errors
        setFieldError(fieldName, undefined);
      }
    },
    [fieldName, fieldError, setFieldError, setFieldTouched],
  );

  const warningBorderStyle = isMisspelled
    ? { borderColor: theme.palette.warning.main }
    : undefined;

  const warningSx = isMisspelled
    ? { "& fieldset": { borderColor: `${theme.palette.warning.main} !important` } }
    : undefined;

  return { onBlur, isMisspelled, warningBorderStyle, warningSx };
};
